'use client';

import {useEffect, useRef} from 'react';
import {usePathname} from 'next/navigation';
import {datadogRum} from '@datadog/browser-rum';
import getGameId from '@/utils/getGameId';
import TagManager from '@sooro-io/react-gtm-module';
import Cookies from 'js-cookie';
import {NavigationManager} from '@sdk-mobile';
import {SKILLZ_APP_COOKIE_NAME} from '@/utils/constant';
import {getUserId} from '@/services/internal/user/getUserId';
type Props = {gtmId: string; env: string; nonce: string; appsFlyerKey: string};

export default function ClientSideHead({gtmId, env, nonce, appsFlyerKey}: Props) {
  const pathname = usePathname();
  const datadogRumInitialized = useRef(false);

  useEffect(() => {
    NavigationManager.initializeScreenTracking();
  }, []);

  useEffect(() => {
    if (gtmId && nonce) {
      TagManager.initialize({gtmId, nonce});
    }
  }, [gtmId, nonce]);

  // TODO - Add User Consent for Gtag and Datadog
  useEffect(() => {
    // Initialize Datadog RUM
    // Note: When running in React Native WebView (window.ddRN = true),
    // we use the RN app's Application ID to enable unified sessions.
    // The @datadog/mobile-react-native-webview component will link
    // web and native sessions to prevent duplicates.
    if (!datadogRumInitialized.current) {
      datadogRumInitialized.current = true;

      // Check if running in RN WebView with Skillz App
      const isInRNWebView = Cookies.get(SKILLZ_APP_COOKIE_NAME) === '1';

      // Use RN app's Application ID and Client Token for unified sessions
      const applicationId = isInRNWebView
        ? '974368e5-e31d-43d2-a9bd-102da36287c7' // Skillz App RN Application ID
        : '3c961121-207b-4931-8de1-3bd8971abc83'; // Web Application ID

      const clientToken = isInRNWebView
        ? 'pub16a3671e95b044ce2d371166b25dbb8f' // Skillz App RN Client Token
        : 'pub906b17ee3c0f14eb2f836ffd08cc8d31'; // Web Client Token

      const service = isInRNWebView ? 'Skillz App' : 'web-sdk';
      datadogRum.init({
        applicationId,
        clientToken,
        // `site` refers to the Datadog site parameter of your organization
        // see https://docs.datadoghq.com/getting_started/site/
        site: 'datadoghq.com',
        service,
        env: env,
        version: process.env.NEXT_PUBLIC_GIT_VERSION,
        sessionSampleRate: 100,
        sessionReplaySampleRate: 20,
        trackUserInteractions: true,
        trackResources: true,
        trackLongTasks: true,
        defaultPrivacyLevel: 'mask-user-input',
        beforeSend: (event) => {
          if (
            event.type === 'error' &&
            event.error?.message?.includes('An error occurred in the Server Components render')
          ) {
            return false; // Ignore redacted Next.js server component errors
          }
          // Benign Next.js RSC-prefetch 404s. A client-side navigation to a
          // middleware-REWRITTEN route (the multicade shell clean URLs — /account/withdraw,
          // /account, … — are rewritten to /games/19369/*) makes the App Router fetch the
          // RSC payload for the CLEAN url; the rewritten route doesn't return a clean RSC
          // response, so it 404s and Next transparently falls back to a successful hard
          // navigation. The nav works — the 404 is pure noise — so drop it instead of
          // flooding RUM resources (and any 4xx monitor) at 100% sampling. Scoped to the
          // `_rsc=` marker so only these prefetch requests are filtered, never real 404s.
          // datadog-rum types `event` loosely here: neither the resource event's own
          // `.resource` nor the error event's embedded `.error.resource` is on the base
          // union member, so read both through one narrow cast.
          type RscFields = {url?: string; status_code?: number};
          const rum = event as {resource?: RscFields; error?: {resource?: RscFields}};
          const rscUrl =
            event.type === 'resource' ? rum.resource?.url : rum.error?.resource?.url;
          const rscStatus =
            event.type === 'resource'
              ? rum.resource?.status_code
              : rum.error?.resource?.status_code;
          if (rscStatus === 404 && rscUrl?.includes('_rsc=')) {
            return false;
          }
          // Expected-and-handled anon-shell 401s. A guest multicade session has no wallet, so
          // the store / limited-time-offer / user reads 401 and the surface DEGRADES on purpose
          // (store/page.tsx EMPTY_STORE fallback, PartnerSdkRuntime LTO suppression). At 100%
          // sampling these flood RUM resources/errors with no signal, so drop them — scoped to
          // those endpoints so a real 401 elsewhere still reports.
          if (rscStatus === 401 && rscUrl && /limited_time_offers|\/v2\/store|\/v1\/user/.test(rscUrl)) {
            return false;
          }
          return true;
        },
      });

      // Start tracking user sessions
      datadogRum.startSessionReplayRecording();
    }

    // Set user identification for session tracking
    // This is critical for linking RN and web sessions
    const userId = getUserId(false); // Don't logout if missing
    if (userId) {
      datadogRum.setUser({
        id: userId,
      });
    }

    // Set context to indicate if running in React Native WebView
    if (typeof window !== 'undefined' && window.ddRN) {
      const skillzAppCookie = Cookies.get(SKILLZ_APP_COOKIE_NAME);
      if (skillzAppCookie === '1') {
        datadogRum.setGlobalContextProperty('skillz.app.webview', true);
      }
    }

    // Log a page view on route change
    const gameId = getGameId();
    if (gameId) {
      datadogRum.setGlobalContextProperty('skillz.sdk.game_id', gameId);
    }
    datadogRum.setGlobalContextProperty('path', pathname);
  }, [pathname, env]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    // Track page view in GTM
    //@ts-expect-error
    window.dataLayer = window.dataLayer || [];
    //@ts-expect-error
    window.dataLayer.push({
      event: 'pageview',
      page_path: pathname,
    });
  }, [pathname]);

  useEffect(() => {
    if (typeof window === 'undefined') return;


    // Injecting the full AppsFlyer script into the DOM
    const script = document.createElement('script');
    script.async = true;
    script.type = 'text/javascript';
    script.text = `
      !function(t,e,n,s,a,c,i,o,p){t.AppsFlyerSdkObject=a,t.AF=t.AF||function(){
      (t.AF.q=t.AF.q||[]).push([Date.now()].concat(Array.prototype.slice.call(arguments)))},
      t.AF.id=t.AF.id||i,t.AF.plugins={},o=e.createElement(n),p=e.getElementsByTagName(n)[0],o.async=1,
      o.src="https://websdk.appsflyer.com?"+(c.length>0?"st="+c.split(",").sort().join(",")+"&":"")+(i.length>0?"af_id="+i:""),
      p.parentNode.insertBefore(o,p)}(window,document,"script",0,"AF","pba",{pba: {webAppId: "${appsFlyerKey}", measurementStatus:true}});
    `;

    if (nonce) {
      script.nonce = nonce;
    }

    document.head.appendChild(script);
  }, [nonce]);

  return <></>;
}
